import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { EvalResult, TestCase, batchRunEval } from "../../services/evals";

interface ActiveRun {
  id: string;
  name: string | null;
  total: number;
  completed: number;
  successful: number;
  failed: number;
  results: EvalResult[];
  testStatuses: Array<{
    testCaseId: string;
    testCaseName: string;
    status: "pending" | "running" | "passed" | "failed";
    score?: number;
    resultId?: string;
  }>;
}

export const useBatchRunState = (
  testCases: TestCase[] | undefined,
  selectedTestCaseIds: string[],
  setSelectedTestCaseIds: (ids: string[]) => void,
  handleTabChange: (tab: string) => void
) => {
  const queryClient = useQueryClient();
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);

  const handleBatchRun = async (runName: string, defaultName: string) => {
    const runId = crypto.randomUUID();
    const finalRunName = runName || defaultName;

    const initialStatuses = selectedTestCaseIds.map(id => {
      const tc = testCases?.find(t => t.id === id);
      return {
        testCaseId: id,
        testCaseName: tc?.name || "Unknown Test",
        status: "pending" as const,
      };
    });

    setActiveRun({
      id: runId,
      name: finalRunName,
      total: selectedTestCaseIds.length,
      completed: 0,
      successful: 0,
      failed: 0,
      results: [],
      testStatuses: initialStatuses,
    });

    handleTabChange("runs");
    toast({ description: "Run started successfully" });

    try {
      const result = await batchRunEval({
        test_case_ids: selectedTestCaseIds,
        run_name: finalRunName,
      });

      if (!result) {
        toast({
          description: "Batch run failed: No results returned",
          variant: "destructive",
        });
        return;
      }

      const resultsList = Array.isArray(result.results) ? result.results : [];
      const finalStatuses = resultsList.map(r => {
        const tc = testCases?.find(t => t.name === r.test_case_name);
        const status: "passed" | "failed" = Math.round(r.overall_score) >= 4 ? "passed" : "failed";
        return {
          testCaseId: tc?.id || "",
          testCaseName: r.test_case_name,
          status,
          score: r.overall_score,
          resultId: r.id,
        };
      });

      setActiveRun({
        id: runId,
        name: finalRunName,
        total: result.total,
        completed: result.total,
        successful: result.successful,
        failed: result.failed,
        results: resultsList,
        testStatuses: finalStatuses,
      });

      queryClient.invalidateQueries({ queryKey: ["evalResults"] });
      toast({
        description: `Batch run completed: ${result.successful} successful, ${result.failed} failed`,
      });
      setSelectedTestCaseIds([]);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast({
        description: `Failed to run batch evaluation: ${message}`,
        variant: "destructive",
      });
      setActiveRun(null);
    }
  };

  return {
    activeRun,
    handleBatchRun,
  };
};
